/**
 * AdminBookings Component
 * Page for viewing all bookings with pagination and filtering
 */

import React, { Component } from 'react';
import AdminWrapper from './AdminWrapper';
import { adminService } from '../../_services/admin.service';
import './Admin.css';

// Booking status mapping
const BOOKING_STATUS = {
    0: { label: 'Pending', className: 'admin-badge-warning' },
    1: { label: 'Booked', className: 'admin-badge-info' },
    2: { label: 'Confirmed', className: 'admin-badge-success' },
    3: { label: 'Cancelled', className: 'admin-badge-danger' },
    4: { label: 'Completed', className: 'admin-badge-success' },
    5: { label: 'Rescheduled', className: 'admin-badge-warning' },
    6: { label: 'No Show', className: 'admin-badge-danger' }
};

// Payment type mapping
const PAYMENT_TYPES = {
    2: 'Card',
    3: 'Cash',
    4: 'Other',
    5: 'BEU Card Reader',
    10: 'Card on File',
    11: 'Voucher',
    12: 'Payment Link',
    13: 'Klarna',
    14: 'Clearpay'
};

class AdminBookings extends Component {
    constructor(props) {
        super(props);
        this.state = {
            bookings: [],
            loading: true,
            error: null,
            search: '',
            status: '',
            salonId: '',
            hairdresserId: '',
            page: 1,
            limit: 20,
            totalPages: 1,
            totalCount: 0,
            grandTotalAmount: 0,
            sortBy: 'bookingDate',
            sortOrder: 'desc',
            // Filter dropdown data
            salons: [],
            professionals: [],
            salonSearch: '',
            professionalSearch: '',
            showSalonDropdown: false,
            showProfessionalDropdown: false,
            loadingSalons: false,
            loadingProfessionals: false
        };
        this.searchTimeout = null;
        this.salonSearchTimeout = null;
        this.professionalSearchTimeout = null;
        this.salonDropdownRef = React.createRef();
        this.professionalDropdownRef = React.createRef();
    }

    componentDidMount() {
        this.fetchBookings();
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside = (event) => {
        if (this.salonDropdownRef.current && !this.salonDropdownRef.current.contains(event.target)) {
            this.setState({ showSalonDropdown: false });
        }
        if (this.professionalDropdownRef.current && !this.professionalDropdownRef.current.contains(event.target)) {
            this.setState({ showProfessionalDropdown: false });
        }
    }

    fetchBookings = async () => {
        const { page, limit, search, status, salonId, hairdresserId, sortBy, sortOrder } = this.state;
        this.setState({ loading: true });

        try {
            const response = await adminService.getBookings({
                page,
                limit,
                search,
                status,
                salonId,
                hairdresserId,
                sortBy,
                sortOrder
            });

            if (response.statusCode === 1) {
                this.setState({
                    bookings: response.responseData.bookings || [],
                    totalPages: response.responseData.pagination?.pages || 1,
                    totalCount: response.responseData.pagination?.total || 0,
                    grandTotalAmount: response.responseData.pagination?.grandTotalAmount || 0,
                    loading: false
                });
            } else {
                this.setState({
                    error: 'Failed to load bookings',
                    loading: false
                });
            }
        } catch (error) {
            console.error('Error fetching bookings:', error);
            this.setState({
                error: 'Failed to load bookings',
                loading: false
            });
        }
    }

    fetchSalons = async (searchTerm = '') => {
        this.setState({ loadingSalons: true });
        try {
            const response = await adminService.getSalons({
                page: 1,
                limit: 50,
                search: searchTerm,
                sortBy: 'salonName',
                sortOrder: 'asc'
            });
            if (response.statusCode === 1) {
                this.setState({
                    salons: response.responseData.salons || [],
                    loadingSalons: false
                });
            }
        } catch (error) {
            console.error('Error fetching salons:', error);
            this.setState({ loadingSalons: false });
        }
    }

    fetchProfessionals = async (searchTerm = '') => {
        this.setState({ loadingProfessionals: true });
        try {
            const response = await adminService.getHairdressers({
                page: 1,
                limit: 50,
                search: searchTerm,
                sortBy: 'name',
                sortOrder: 'asc'
            });
            if (response.statusCode === 1) {
                this.setState({
                    professionals: response.responseData.hairdressers || [],
                    loadingProfessionals: false
                });
            }
        } catch (error) {
            console.error('Error fetching professionals:', error);
            this.setState({ loadingProfessionals: false });
        }
    }

    handleSearchChange = (e) => {
        const search = e.target.value;
        this.setState({ search, page: 1 });

        // Debounce search
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
        this.searchTimeout = setTimeout(() => {
            // Only search if empty (to clear) or 2+ characters (to avoid scanning entire collection)
            if (search.length === 0 || search.length >= 2) {
                this.fetchBookings();
            }
        }, 500);
    }

    handleStatusChange = (e) => {
        const status = e.target.value;
        this.setState({ status, page: 1 }, this.fetchBookings);
    }

    handleSalonFocus = () => {
        this.setState({ showSalonDropdown: true });
        if (this.state.salons.length === 0) {
            this.fetchSalons();
        }
    }

    handleSalonSearchChange = (e) => {
        const salonSearch = e.target.value;
        this.setState({ salonSearch });

        if (this.salonSearchTimeout) {
            clearTimeout(this.salonSearchTimeout);
        }
        this.salonSearchTimeout = setTimeout(() => {
            this.fetchSalons(salonSearch);
        }, 300);
    }

    handleSalonSelect = (salon) => {
        this.setState({
            salonId: salon ? salon._id : '',
            salonSearch: salon ? salon.salonName : '',
            showSalonDropdown: false,
            page: 1
        }, this.fetchBookings);
    }

    handleProfessionalFocus = () => {
        this.setState({ showProfessionalDropdown: true });
        if (this.state.professionals.length === 0) {
            this.fetchProfessionals();
        }
    }

    handleProfessionalSearchChange = (e) => {
        const professionalSearch = e.target.value;
        this.setState({ professionalSearch });

        if (this.professionalSearchTimeout) {
            clearTimeout(this.professionalSearchTimeout);
        }
        this.professionalSearchTimeout = setTimeout(() => {
            this.fetchProfessionals(professionalSearch);
        }, 300);
    }

    handleProfessionalSelect = (professional) => {
        this.setState({
            hairdresserId: professional ? professional._id : '',
            professionalSearch: professional ? professional.name : '',
            showProfessionalDropdown: false,
            page: 1
        }, this.fetchBookings);
    }

    clearFilters = () => {
        this.setState({
            search: '',
            status: '',
            salonId: '',
            hairdresserId: '',
            salonSearch: '',
            professionalSearch: '',
            page: 1
        }, this.fetchBookings);
    }

    handlePageChange = (newPage) => {
        this.setState({ page: newPage }, this.fetchBookings);
    }

    getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }

    formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    }

    formatTime = (slots) => {
        if (!slots || slots.length === 0) return 'N/A';
        // Slots are in minutes from midnight
        const startSlot = slots[0];
        const hours = Math.floor(startSlot / 60);
        const minutes = startSlot % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    formatCurrency = (amount, symbol) => {
        if (amount === undefined || amount === null) return 'N/A';
        return `${symbol || '£'}${parseFloat(amount).toFixed(2)}`;
    }

    getStatusBadge = (status) => {
        const statusInfo = BOOKING_STATUS[status] || { label: 'Unknown', className: 'admin-badge-secondary' };
        return (
            <span className={`admin-badge ${statusInfo.className}`}>
                {statusInfo.label}
            </span>
        );
    }

    renderSaleItems = (booking) => {
        const hasServices = booking.servicesBooked && booking.servicesBooked.length > 0;
        const hasProducts = booking.products && booking.products.length > 0;
        const hasVouchers = booking.vouchers && booking.vouchers.length > 0;

        const typeCount = [hasServices, hasProducts, hasVouchers].filter(Boolean).length;

        // No items
        if (typeCount === 0) {
            return <span style={{ color: '#999', fontSize: '13px' }}>No items</span>;
        }

        // Only one type - show item names directly
        if (typeCount === 1) {
            if (hasServices) {
                const services = booking.servicesBooked;
                if (services.length === 1) {
                    return <span>{services[0].serviceName || 'Service'}</span>;
                }
                return (
                    <span>
                        {services[0].serviceName || 'Service'}
                        <span style={{ color: '#666', marginLeft: '4px' }}>+{services.length - 1} more</span>
                    </span>
                );
            }
            if (hasProducts) {
                const products = booking.products;
                if (products.length === 1) {
                    return <span>{products[0].productName || 'Product'}</span>;
                }
                return (
                    <span>
                        {products[0].productName || 'Product'}
                        <span style={{ color: '#666', marginLeft: '4px' }}>+{products.length - 1} more</span>
                    </span>
                );
            }
            if (hasVouchers) {
                const vouchers = booking.vouchers;
                if (vouchers.length === 1) {
                    return <span>Voucher {vouchers[0].code || ''}</span>;
                }
                return (
                    <span>
                        Voucher {vouchers[0].code || ''}
                        <span style={{ color: '#666', marginLeft: '4px' }}>+{vouchers.length - 1} more</span>
                    </span>
                );
            }
        }

        // Multiple types - show pills
        const badges = [];

        if (hasServices) {
            const count = booking.servicesBooked.length;
            const names = booking.servicesBooked.map(s => s.serviceName).join(', ');
            badges.push(
                <span
                    key="services"
                    className="admin-sale-badge admin-sale-badge-service"
                    title={names}
                >
                    {count} {count === 1 ? 'Service' : 'Services'}
                </span>
            );
        }

        if (hasProducts) {
            const count = booking.products.length;
            const names = booking.products.map(p => p.productName).join(', ');
            badges.push(
                <span
                    key="products"
                    className="admin-sale-badge admin-sale-badge-product"
                    title={names}
                >
                    {count} {count === 1 ? 'Product' : 'Products'}
                </span>
            );
        }

        if (hasVouchers) {
            const count = booking.vouchers.length;
            const codes = booking.vouchers.map(v => v.code || 'Voucher').join(', ');
            badges.push(
                <span
                    key="vouchers"
                    className="admin-sale-badge admin-sale-badge-voucher"
                    title={codes}
                >
                    {count} {count === 1 ? 'Voucher' : 'Vouchers'}
                </span>
            );
        }

        return <div className="admin-sale-badges">{badges}</div>;
    }

    getPaymentMethods = (transaction) => {
        if (!transaction || transaction.length === 0) return 'N/A';
        const methods = transaction.map(t => PAYMENT_TYPES[t.paymentType] || 'Unknown').filter(Boolean);
        if (methods.length === 0) return 'N/A';
        if (methods.length === 1) return methods[0];
        return methods.join(', ');
    }

    hasActiveFilters = () => {
        const { search, status, salonId, hairdresserId } = this.state;
        return search || status || salonId || hairdresserId;
    }

    render() {
        const {
            bookings, loading, error, search, status, page, totalPages, totalCount, grandTotalAmount,
            salons, professionals, salonSearch, professionalSearch,
            showSalonDropdown, showProfessionalDropdown, loadingSalons, loadingProfessionals
        } = this.state;

        return (
            <AdminWrapper>
                <div className="admin-page-header">
                    <h1 className="admin-page-title">Bookings</h1>
                    <p className="admin-page-subtitle">View all bookings across the platform</p>
                </div>

                <div className="admin-table-container">
                    <div className="admin-table-header">
                        <h3 className="admin-table-title">
                            {totalCount.toLocaleString()} {totalCount === 1 ? 'Booking' : 'Bookings'} {grandTotalAmount > 0 && `• £${grandTotalAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        </h3>
                    </div>

                    {/* Filter Row */}
                    <div className="admin-filters-row">
                        {/* Status Filter */}
                        <div className="admin-filter-group">
                            <label className="admin-filter-label">Status</label>
                            <select
                                className="admin-filter-select"
                                value={status}
                                onChange={this.handleStatusChange}
                            >
                                <option value="">All Statuses</option>
                                <option value="0">Pending</option>
                                <option value="1">Booked</option>
                                <option value="2">Confirmed</option>
                                <option value="3">Cancelled</option>
                                <option value="4">Completed</option>
                                <option value="5">Rescheduled</option>
                                <option value="6">No Show</option>
                            </select>
                        </div>

                        {/* Salon Filter */}
                        <div className="admin-filter-group" ref={this.salonDropdownRef}>
                            <label className="admin-filter-label">Salon</label>
                            <div className="admin-filter-dropdown">
                                <input
                                    type="text"
                                    className="admin-filter-input"
                                    placeholder="Search salons..."
                                    value={salonSearch}
                                    onChange={this.handleSalonSearchChange}
                                    onFocus={this.handleSalonFocus}
                                />
                                {salonSearch && (
                                    <button
                                        className="admin-filter-clear"
                                        onClick={() => this.handleSalonSelect(null)}
                                    >
                                        ×
                                    </button>
                                )}
                                {showSalonDropdown && (
                                    <div className="admin-filter-dropdown-menu">
                                        {loadingSalons ? (
                                            <div className="admin-filter-dropdown-loading">Loading...</div>
                                        ) : salons.length === 0 ? (
                                            <div className="admin-filter-dropdown-empty">No salons found</div>
                                        ) : (
                                            salons.map(salon => (
                                                <div
                                                    key={salon._id}
                                                    className="admin-filter-dropdown-item"
                                                    onClick={() => this.handleSalonSelect(salon)}
                                                >
                                                    {salon.salonName}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Professional Filter */}
                        <div className="admin-filter-group" ref={this.professionalDropdownRef}>
                            <label className="admin-filter-label">Professional</label>
                            <div className="admin-filter-dropdown">
                                <input
                                    type="text"
                                    className="admin-filter-input"
                                    placeholder="Search professionals..."
                                    value={professionalSearch}
                                    onChange={this.handleProfessionalSearchChange}
                                    onFocus={this.handleProfessionalFocus}
                                />
                                {professionalSearch && (
                                    <button
                                        className="admin-filter-clear"
                                        onClick={() => this.handleProfessionalSelect(null)}
                                    >
                                        ×
                                    </button>
                                )}
                                {showProfessionalDropdown && (
                                    <div className="admin-filter-dropdown-menu">
                                        {loadingProfessionals ? (
                                            <div className="admin-filter-dropdown-loading">Loading...</div>
                                        ) : professionals.length === 0 ? (
                                            <div className="admin-filter-dropdown-empty">No professionals found</div>
                                        ) : (
                                            professionals.map(pro => (
                                                <div
                                                    key={pro._id}
                                                    className="admin-filter-dropdown-item"
                                                    onClick={() => this.handleProfessionalSelect(pro)}
                                                >
                                                    <span>{pro.name}</span>
                                                    {pro.salonName && (
                                                        <span className="admin-filter-dropdown-subtitle">{pro.salonName}</span>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Client / Booking ID Search */}
                        <div className="admin-filter-group admin-filter-group-grow">
                            <label className="admin-filter-label">Search</label>
                            <input
                                type="text"
                                className="admin-filter-input"
                                placeholder="Client name, email, or booking ID..."
                                value={search}
                                onChange={this.handleSearchChange}
                            />
                        </div>

                        {/* Clear Filters */}
                        {this.hasActiveFilters() && (
                            <div className="admin-filter-group admin-filter-group-clear">
                                <label className="admin-filter-label">&nbsp;</label>
                                <button
                                    className="admin-btn admin-btn-secondary admin-btn-sm"
                                    onClick={this.clearFilters}
                                >
                                    Clear Filters
                                </button>
                            </div>
                        )}
                    </div>

                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <div className="admin-loading-spinner" style={{ margin: '0 auto 16px' }}></div>
                            <p style={{ color: '#666' }}>Loading bookings...</p>
                        </div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <p style={{ color: '#ff6b6b', marginBottom: '16px' }}>{error}</p>
                            <button className="admin-btn admin-btn-secondary" onClick={this.fetchBookings}>
                                Retry
                            </button>
                        </div>
                    ) : bookings.length === 0 ? (
                        <div className="admin-empty-state">
                            <div className="admin-empty-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="48" height="48">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                            </div>
                            <h3 className="admin-empty-title">No bookings found</h3>
                            <p className="admin-empty-text">
                                {this.hasActiveFilters() ? 'Try adjusting your filters' : 'No bookings have been made yet'}
                            </p>
                        </div>
                    ) : (
                        <>
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Client</th>
                                        <th>Professional</th>
                                        <th>Salon</th>
                                        <th>Date & Time</th>
                                        <th>Sale Items</th>
                                        <th>Amount</th>
                                        <th>Payment Method</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bookings.map(booking => (
                                        <tr
                                            key={booking._id}
                                            onClick={() => this.props.history.push(`/beu-admin-x7k9/bookings/${booking._id}`)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    {booking.clientAvatar ? (
                                                        <img
                                                            src={booking.clientAvatar}
                                                            alt={booking.clientName}
                                                            className="admin-table-avatar"
                                                        />
                                                    ) : (
                                                        <div className="admin-table-avatar-placeholder">
                                                            {this.getInitials(booking.clientName)}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div style={{ fontWeight: '500' }}>
                                                            {booking.clientName || 'Unknown Client'}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                                            {booking.clientEmail || ''}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{booking.hairdresserName || 'N/A'}</td>
                                            <td>{booking.salonName || 'N/A'}</td>
                                            <td>
                                                <div>
                                                    <div style={{ fontWeight: '500' }}>
                                                        {this.formatDate(booking.bookingDate)}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                        {this.formatTime(booking.bookingSlot)}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {this.renderSaleItems(booking)}
                                            </td>
                                            <td>
                                                {this.formatCurrency(booking.totalAmount, booking.currencySymbol)}
                                            </td>
                                            <td>
                                                {this.getPaymentMethods(booking.transaction)}
                                            </td>
                                            <td>
                                                {this.getStatusBadge(booking.bookingStatus)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {totalPages > 1 && (
                                <div className="admin-pagination">
                                    <div className="admin-pagination-info">
                                        Page {page} of {totalPages} ({totalCount.toLocaleString()} total)
                                    </div>
                                    <div className="admin-pagination-controls">
                                        <button
                                            className="admin-pagination-btn"
                                            onClick={() => this.handlePageChange(page - 1)}
                                            disabled={page === 1}
                                        >
                                            Previous
                                        </button>
                                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (page <= 3) {
                                                pageNum = i + 1;
                                            } else if (page >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = page - 2 + i;
                                            }
                                            return (
                                                <button
                                                    key={pageNum}
                                                    className={`admin-pagination-btn ${page === pageNum ? 'active' : ''}`}
                                                    onClick={() => this.handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                        <button
                                            className="admin-pagination-btn"
                                            onClick={() => this.handlePageChange(page + 1)}
                                            disabled={page === totalPages}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </AdminWrapper>
        );
    }
}

export default AdminBookings;
